"use client";

import { useState } from "react";
import { Braces, FunctionSquare } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SampleExplanation } from "./SampleExplanation";
import {
  FUNCTION_LANGUAGES,
  SIGNATURE_TOLERANCE_DEFAULT,
  dimensionsOf,
  holdsDouble,
  type FunctionLanguage,
  type FunctionSignature,
  type FunctionStarters,
} from "../../lib/function-signature";
import { splitCalls } from "../../lib/function-value";

/**
 * A function problem's signature, as a student reads it - `EX-FNC04` (the
 * setter's Preview), `EX-FNC05` (the practice and contest pages), board D of
 * the Wave 36 plan.
 *
 * In place of the input and output formats: the function to write in one of
 * the five languages (its starter, the text the editor opens on), each
 * parameter with its type in words, what it returns, and how the answer is
 * compared. The language is the card's own until a caller owns it
 * (`language` with `onLanguageChange`). `EX-FNC05`: `language` alone is the
 * editor's, which the card follows; a tab pressed here only shows another
 * language's text, until the editor's language changes again. `EX-FNC08`:
 * `tone="dark"` for the exam workspace, whose statement sits on the dark
 * player.
 */

const LANGUAGE_LABEL: Record<FunctionLanguage, string> = {
  cpp: "C++",
  java: "Java",
  python: "Python",
  javascript: "JavaScript",
  c: "C",
};

export function functionLanguageLabel(language: FunctionLanguage): string {
  return LANGUAGE_LABEL[language];
}

/**
 * The `ফাংশন` chip: on the meta row, and (`EX-FNC05`, `sm`) beside a title in
 * the library and on a contest's problem list, as the run-twice and
 * interactive tags sit there.
 */
export function FunctionTag({ size = "md" }: { size?: "sm" | "md" }) {
  const t = useTranslations("judge.function");
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-primary/30 bg-primary/[0.06] font-semibold text-[#C93D12]",
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-0.5 text-xs",
      )}
      title={t("tagTitle")}
      data-testid="function-tag"
    >
      <FunctionSquare aria-hidden className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {t("tag")}
    </span>
  );
}

/** `EX-FNC08`: the card's colours on the light pages and on the dark exam player. */
const CARD_TONE = {
  light: {
    section: "border-primary/25 bg-primary/[0.03]",
    title: "text-neutral-900",
    group: "bg-white shadow-sm",
    tab: "segment",
    code: "border-primary/15 bg-white text-neutral-900",
    list: "text-neutral-700",
    name: "bg-white text-neutral-900",
    type: "text-neutral-500",
    strong: "text-neutral-900",
    rule: "border-primary/10",
    notes: "text-neutral-600",
  },
  dark: {
    section: "border-primary/30 bg-primary/[0.06]",
    title: "text-white",
    group: "bg-white/5",
    tab: "segmentDark",
    code: "border-white/10 bg-[#0B0B0C] text-white/90",
    list: "text-white/75",
    name: "bg-white/10 text-white",
    type: "text-white/45",
    strong: "text-white",
    rule: "border-white/10",
    notes: "text-white/55",
  },
} as const;

export function FunctionSignatureCard({
  signature,
  starters,
  language,
  onLanguageChange,
  tone = "light",
}: {
  signature: FunctionSignature;
  starters: FunctionStarters;
  language?: FunctionLanguage;
  onLanguageChange?: (language: FunctionLanguage) => void;
  tone?: "light" | "dark";
}) {
  const c = CARD_TONE[tone];
  const t = useTranslations("judge.function");
  const [ownLanguage, setOwnLanguage] = useState<FunctionLanguage>("cpp");
  /** A tab pressed while the editor owns the language, and which editor language it was pressed over. */
  const [peek, setPeek] = useState<{ over: FunctionLanguage; shown: FunctionLanguage } | null>(null);
  const followed = language && !onLanguageChange ? language : null;
  const shown = followed ? (peek && peek.over === followed ? peek.shown : followed) : (language ?? ownLanguage);
  const choose =
    onLanguageChange ?? (followed ? (next: FunctionLanguage) => setPeek({ over: followed, shown: next }) : setOwnLanguage);
  const arrayAnswer = dimensionsOf(signature.returns) > 0;

  return (
    <section
      aria-label={t("cardTitle")}
      className={cn("mt-5 space-y-3 rounded-2xl border p-4", c.section)}
      data-testid="function-signature-card"
      data-tone={tone}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn("flex items-center gap-2 text-sm font-bold", c.title)}>
          <Braces aria-hidden className="h-4 w-4 text-primary" />
          {t("cardTitle")}
        </p>
        <div role="group" aria-label={t("languageLabel")} className={cn("flex max-w-full gap-1 overflow-x-auto rounded-full p-1", c.group)}>
          {FUNCTION_LANGUAGES.map((key) => (
            <Button
              key={key}
              type="button"
              variant={c.tab}
              size="chip"
              aria-pressed={shown === key}
              onClick={() => choose(key)}
            >
              {LANGUAGE_LABEL[key]}
            </Button>
          ))}
        </div>
      </div>
      <pre
        className={cn("overflow-x-auto rounded-xl border px-3.5 py-3 font-mono text-[13px] leading-relaxed", c.code)}
        data-testid="function-signature-code"
      >
        {starters[shown].replace(/\n+$/, "")}
      </pre>
      <ul className={cn("grid gap-1.5 text-sm", c.list)}>
        {signature.params.map((param) => (
          <li key={param.name} className="flex flex-wrap items-baseline gap-x-2">
            <code className={cn("rounded px-1.5 py-0.5 font-mono text-[13px]", c.name)}>{param.name}</code>
            <span className={cn("font-mono text-xs", c.type)}>{param.type}</span>
            <span>{t(`type_${typeKey(param.type)}`)}</span>
          </li>
        ))}
        <li className={cn("flex flex-wrap items-baseline gap-x-2 border-t pt-1.5", c.rule)}>
          <span className={cn("font-semibold", c.strong)}>{t("returns")}</span>
          <span className={cn("font-mono text-xs", c.type)}>{signature.returns}</span>
          <span>{t(`type_${typeKey(signature.returns)}`)}</span>
        </li>
      </ul>
      <ul className={cn("grid gap-1 text-xs leading-relaxed", c.notes)}>
        <li>{t("noInputNote")}</li>
        {arrayAnswer && signature.unordered ? <li>{t("unorderedNote")}</li> : null}
        {holdsDouble(signature.returns) ? (
          <li>{t("toleranceNote", { tolerance: String(signature.tolerance ?? SIGNATURE_TOLERANCE_DEFAULT) })}</li>
        ) : null}
      </ul>
    </section>
  );
}

/** The words for a type: one key per scalar, its array and its grid. */
function typeKey(type: FunctionSignature["returns"]): string {
  return type.replace("[][]", "_grid").replace("[]", "_list");
}

/**
 * One example of a function problem, LeetCode's way: each call's arguments as
 * `name = value`, then what the function returns for it.
 */
export function FunctionSample({
  signature,
  input,
  output,
  index,
  explanation,
  tone = "light",
}: {
  signature: FunctionSignature;
  input: string;
  output: string;
  index: number;
  explanation?: string | null;
  /** `EX-FNC08`: the exam workspace's dark player. */
  tone?: "light" | "dark";
}) {
  const t = useTranslations("judge.function");
  const calls = splitCalls(signature, input, output);
  const dark = tone === "dark";
  return (
    <div
      className={cn("space-y-2 rounded-xl border p-3.5", dark ? "border-white/10 bg-white/[0.02]" : "border-[rgb(var(--border))]")}
      data-testid="function-sample"
    >
      <p className={cn("text-xs font-bold", dark ? "text-white/80" : "text-neutral-800")}>{t("exampleN", { idx: index + 1 })}</p>
      {calls.map((call, callIndex) => (
        <div
          key={callIndex}
          className={cn(
            "grid gap-1.5 font-mono text-[13px]",
            callIndex > 0 && (dark ? "border-t border-white/10 pt-2" : "border-t border-neutral-100 pt-2"),
          )}
        >
          {calls.length > 1 ? (
            <p className={cn("font-sans text-[11px] font-semibold", dark ? "text-white/45" : "text-neutral-500")}>
              {t("callN", { idx: callIndex + 1 })}
            </p>
          ) : null}
          <div
            className={cn(
              "grid gap-0.5 overflow-x-auto rounded-lg px-3 py-2",
              dark ? "bg-[#0B0B0C] text-white/85" : "bg-neutral-50 text-neutral-800",
            )}
          >
            {signature.params.map((param, paramIndex) => (
              <span key={param.name} className="whitespace-pre">
                <span className={dark ? "text-white/40" : "text-neutral-400"}>{param.name} = </span>
                {call.params[paramIndex]}
              </span>
            ))}
          </div>
          <p className={cn("whitespace-pre font-sans text-xs", dark ? "text-white/55" : "text-neutral-600")}>
            <span className={cn("font-semibold", dark ? "text-white/80" : "text-neutral-800")}>{t("output")}</span>{" "}
            <span className={cn("font-mono text-[13px]", dark ? "text-white" : "text-neutral-900")}>{call.answer}</span>
          </p>
        </div>
      ))}
      <SampleExplanation html={explanation} tone={tone} />
    </div>
  );
}
